import React, { useCallback, useEffect, useState } from "react";
import { BsChevronLeft, BsChevronRight, BsX } from "react-icons/bs";
import { FiRefreshCw, FiSearch } from "react-icons/fi";
import axiosToken from "../../api/axiosClient";
import { roleLabel } from "../../utils/permissions";

const TABS = [
  { value: "equipe", label: "Équipe" },
  { value: "clients", label: "Clients et visiteurs" },
];

const EMPTY_FILTERS = {
  search: "",
  action: "",
  success: "",
  startDate: "",
  endDate: "",
};

const ENTITY_LABELS = {
  user: "Compte",
  salon: "Salon",
  category: "Catégorie",
  service: "Prestation",
  appointment: "Rendez-vous",
  appointment_service: "Prestation réservée",
  assignment: "Assignation",
  payment: "Paiement",
  plan: "Formule",
  subscription: "Abonnement",
  giftcard: "Carte cadeau",
  product: "Produit",
  alert: "Alerte de stock",
};

// Date réelle de l'action, affichée à l'heure de l'appareil
const formatDateTime = (value) =>
  new Date(value).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const actorLabel = (log) => {
  if (log.actorType === "visiteur") return "Visiteur";
  return roleLabel(log.actorRole) || (log.actorType === "client" ? "Client" : "Équipe");
};

const inputClass =
  "px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 font-nunito text-[14px] bg-white";

export default function ActivityLogPage() {
  const [scope, setScope] = useState("equipe");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ items: [], total: 0, totalPages: 1 });
  const [retention, setRetention] = useState(12);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axiosToken
      .get("/activity-logs/actions")
      .then((response) => setActions(response.data?.data || []))
      .catch(() => setActions([]));
  }, []);

  // Recherche appliquée après une courte pause de saisie
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search }));
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { scope, page, limit: 25 };
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "") params[key] = value;
      });
      const response = await axiosToken.get("/activity-logs", { params });
      setResult(response.data?.data || { items: [], total: 0, totalPages: 1 });
      setRetention(response.data?.retentionMonths || 12);
    } catch (err) {
      setError(err?.response?.data?.error || "Impossible de charger le journal d'activité.");
    } finally {
      setLoading(false);
    }
  }, [scope, page, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setSearch("");
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const changeScope = (value) => {
    setScope(value);
    setPage(1);
  };

  return (
    <div className="md:p-6">
      {/* En-tête */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h2 className="font-poppins font-semibold text-[18px] text-title">
              Journal d'activité
            </h2>
            <span className="px-3 py-1 bg-pink-50 text-pink-700 font-nunito text-[14px]">
              {result.total} entrée{result.total > 1 ? "s" : ""}
            </span>
          </div>
          <p className="font-nunito text-[14px] text-text">
            Actions qui modifient des données et connexions. Les entrées sont
            conservées {retention} mois.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 font-nunito text-[14px] font-semibold hover:bg-gray-50"
        >
          <FiRefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Actualiser
        </button>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 mb-4 border-b border-gray-200">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => changeScope(tab.value)}
            className={`px-4 py-2 font-poppins font-medium text-[15px] border-b-2 -mb-px transition-colors ${
              scope === tab.value
                ? "border-pink-600 text-pink-700"
                : "border-transparent text-text hover:text-title"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filtres */}
      <div className="bg-pink-50/40 border border-pink-100 p-4 mb-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
        <label className="relative xl:col-span-2">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-pink-600" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom, e-mail, action, n° d'objet, IP…"
            className={`${inputClass} w-full pl-9`}
          />
        </label>
        <select
          value={filters.action}
          onChange={(e) => updateFilter("action", e.target.value)}
          className={inputClass}
        >
          <option value="">Toutes les actions</option>
          {actions.map((a) => (
            <option key={a.action} value={a.action}>
              {a.label}
            </option>
          ))}
        </select>
        <select
          value={filters.success}
          onChange={(e) => updateFilter("success", e.target.value)}
          className={inputClass}
        >
          <option value="">Tous les résultats</option>
          <option value="true">Réussies</option>
          <option value="false">Échouées</option>
        </select>
        <input
          type="date"
          value={filters.startDate}
          onChange={(e) => updateFilter("startDate", e.target.value)}
          className={inputClass}
          title="Du"
        />
        <input
          type="date"
          value={filters.endDate}
          onChange={(e) => updateFilter("endDate", e.target.value)}
          className={inputClass}
          title="Au"
        />
        <div className="xl:col-span-6 flex justify-end">
          <button
            onClick={resetFilters}
            className="font-nunito text-[14px] text-pink-700 hover:underline"
          >
            Réinitialiser les filtres
          </button>
        </div>
      </div>

      {error && (
        <p className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 font-nunito text-[14px]">
          {error}
        </p>
      )}

      {/* Tableau */}
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="font-poppins font-semibold text-[14px] text-title">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Auteur</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Objet</th>
              <th className="px-4 py-3">Résultat</th>
              <th className="px-4 py-3">Adresse IP</th>
            </tr>
          </thead>
          <tbody className="font-nunito text-[14px] text-text">
            {!loading && result.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                  Aucune action pour ces critères.
                </td>
              </tr>
            )}
            {result.items.map((log) => (
              <tr
                key={log.id}
                onClick={() => setSelected(log)}
                className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(log.createdAt)}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-title">{log.actorName || "Inconnu"}</div>
                  <div className="text-[12px] text-gray-500">
                    {actorLabel(log)}
                    {log.actorEmail ? ` · ${log.actorEmail}` : ""}
                  </div>
                </td>
                <td className="px-4 py-3">{log.label}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {log.entityType
                    ? `${ENTITY_LABELS[log.entityType] || log.entityType}${log.entityId ? ` n° ${log.entityId}` : ""}`
                    : "—"}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-1 text-[12px] font-semibold ${
                      log.success ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                    }`}
                  >
                    {log.success ? "Réussie" : "Échouée"}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-500">{log.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 font-nunito text-[14px] text-text">
        <span>
          Page {page} sur {result.totalPages}
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            className="p-2 border border-gray-300 disabled:opacity-40"
            title="Page précédente"
          >
            <BsChevronLeft />
          </button>
          <button
            onClick={() => setPage((p) => Math.min(result.totalPages, p + 1))}
            disabled={page >= result.totalPages || loading}
            className="p-2 border border-gray-300 disabled:opacity-40"
            title="Page suivante"
          >
            <BsChevronRight />
          </button>
        </div>
      </div>

      {/* Détail d'une action */}
      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h3 className="font-poppins font-semibold text-[18px] text-title">
                  {selected.label}
                </h3>
                <p className="font-nunito text-[14px] text-text mt-1">
                  {formatDateTime(selected.createdAt)}
                </p>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-gray-100">
                <BsX size={24} />
              </button>
            </div>
            <dl className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3 font-nunito text-[14px]">
              <dt className="text-gray-500">Auteur</dt>
              <dd className="sm:col-span-2 text-title">
                {selected.actorName || "Inconnu"} ({actorLabel(selected)})
                {selected.actorEmail ? ` · ${selected.actorEmail}` : ""}
              </dd>
              <dt className="text-gray-500">Résultat</dt>
              <dd className="sm:col-span-2">
                {selected.success ? "Réussie" : "Échouée"} (code {selected.statusCode})
                {selected.errorMessage ? ` : ${selected.errorMessage}` : ""}
              </dd>
              <dt className="text-gray-500">Objet</dt>
              <dd className="sm:col-span-2">
                {selected.entityType
                  ? `${ENTITY_LABELS[selected.entityType] || selected.entityType}${selected.entityId ? ` n° ${selected.entityId}` : ""}`
                  : "—"}
              </dd>
              <dt className="text-gray-500">Requête</dt>
              <dd className="sm:col-span-2 font-mono text-[13px]">
                {selected.method} {selected.path}
              </dd>
              <dt className="text-gray-500">Adresse IP</dt>
              <dd className="sm:col-span-2">{selected.ip || "—"}</dd>
              <dt className="text-gray-500">Appareil</dt>
              <dd className="sm:col-span-2 break-words text-[13px]">{selected.userAgent || "—"}</dd>
              <dt className="text-gray-500">Données envoyées</dt>
              <dd className="sm:col-span-2">
                {selected.details ? (
                  <pre className="bg-gray-50 border border-gray-200 p-3 text-[12px] overflow-x-auto whitespace-pre-wrap break-words">
                    {JSON.stringify(selected.details, null, 2)}
                  </pre>
                ) : (
                  "—"
                )}
                <p className="text-[12px] text-gray-500 mt-1">
                  Les mots de passe et codes de vérification ne sont jamais enregistrés.
                </p>
              </dd>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}
